import{TranslationStateEnum}from'@graphql/__generated__/globalTypes';/**
 * What a translation status says, in the three lines a practice reads:
 *
 *   Englisch – Teilweise übersetzt
 *   Inhalte: 3 von 5
 *   Videos: 1 von 2
 *
 * Built here rather than in the badge, because two surfaces show exactly this: the badge's
 * tooltip (joined with newlines, since react-tooltip renders `data-tooltip-content` as plain
 * text) and the info modal a click opens, which a tablet needs because it has no hover at all.
 * Two builders would drift, and the drift would show up as a tooltip and a modal disagreeing
 * about the same block.
 *
 * The modal's two readings of `source` live here too - `sourceNoteMessageId` for the sentence and
 * `sourceWarningMessageId` for the warning - because both have to fall silent on an untranslated
 * status, and split across two files that shared rule is a thing to forget in one of them.
 *//** Everything the lines, the source note and the breakdown table need. */export const stateMessageId=state=>{switch(state){case TranslationStateEnum.COMPLETE:return'translation status complete';case TranslationStateEnum.PARTIAL:return'translation status partial';case TranslationStateEnum.NONE:default:return'translation status none';}};/*
 * The badge carries NO source text any more (Marcel: "Der Text für \"Eigene\", \"DR CLIP\" und
 * \"Gemischt\" soll aus den badges entfernt werden", and "Keine kann auch weg"). What is left is
 * the flag and the colour: which language, and how far along.
 *
 * `labelMessageId` is gone rather than made to return null everywhere - a function whose every
 * branch answers "nothing" is a function to delete. Where the wording came from is now only said
 * where there is room to say it properly: `sourceNoteMessageId` in the info modal, and the
 * modal's DR CLIP / Eigene table for a mixed status.
 *
 * The consequence to keep in mind is accessibility: with the text gone, a badge's only visible
 * content is a flag, so the accessible name has to carry the language AND the state. See
 * LanguageBadge.tsx, where it is built from `translationStatusLines`' first line rather than from
 * the locale alone.
 *//**
 * How much of a language's present translation each side supplied - the only honest input for the
 * two functions below.
 *
 * NOT `source`. That field is NULL for a clone (state_consultation, state_information), which is
 * every block of a consultation, so a consultation's block always looked "mixed" and got the mixed
 * sentence even when every translation it had came from DR CLIP. Marcel found exactly that: a
 * table reading DR CLIP 0/2 and Eigene 0/0 under the words "teils von Ihnen".
 *//**
 * The sentence the info modal adds under the breakdown table, or null for no sentence.
 *
 * Only ever about the PRACTICE'S share, because that is the only thing the table cannot say: that
 * a wording of your own is always the one used, and DR CLIP shipping its own later does not
 * displace it. Where the practice supplied nothing there is no such rule to state, and the table
 * has already said whose the translations are - so no sentence at all.
 *
 * That is a deliberate narrowing. It used to print "Alle Übersetzungen kommen von DR CLIP" and its
 * partial variant, which the table now says in numbers, and a mixed sentence keyed on `source`,
 * which was wrong wherever `source` is null.
 */export const sourceNoteMessageId=_ref=>{let{state,officeCount,systemCount}=_ref;if(state===TranslationStateEnum.NONE)return null;if(officeCount===0)return null;return systemCount===0?'translation source note office':'translation source note mixed';};/**
 * The warning beneath it - only where generating the missing translations would hand a unit to the
 * practice for good.
 *
 * Everything present is DR CLIP's, and something is still missing: what the modal's own button
 * would produce is the PRACTICE'S wording, and from then on `MediaFile#resolved_vtt_tracks` and
 * the `practice_override` reader both prefer it, so DR CLIP translating those units later does not
 * reach this practice any more. A consequence of pressing the button that is invisible at the
 * moment of pressing it.
 *
 * Where the practice already has something the note above states the same rule, and where nothing
 * is missing there is nothing to generate.
 */export const sourceWarningMessageId=_ref2=>{let{state,officeCount}=_ref2;return officeCount===0&&state===TranslationStateEnum.PARTIAL?'translation source warning own precedence':null;};/**
 * One string per line, heading first. Never empty - the heading is always there.
 *
 * A pair whose total is 0 gets no line at all: a block without a video must not read
 * "Videos: 0 von 0", which states nothing and reads like a defect. The same guard covers the
 * text pair, which cannot be 0 today (every block has a validated `name`) but costs nothing.
 *
 * The counts stay separate interpolations rather than a pre-formatted "3 von 5", so a
 * translation can reorder them.
 */export const translationStatusLines=(status,intl)=>{const lines=[intl.formatMessage({id:'translation status tooltip heading'},{locale:intl.formatMessage({id:"language ".concat(status.locale)}),state:intl.formatMessage({id:stateMessageId(status.state)})})];if(status.textTotalCount>0){lines.push(intl.formatMessage({id:'translation status tooltip contents'},{translated:status.textTranslatedCount,total:status.textTotalCount}));}if(status.videoTotalCount>0){lines.push(intl.formatMessage({id:'translation status tooltip videos'},{translated:status.videoTranslatedCount,total:status.videoTotalCount}));}return lines;};